import { supabase } from '@/lib/supabase';
import { ProviderServiceError } from './errors';
import { Provider, CreateProviderInput, UpdateProviderInput } from '@/features/providers/types';
import { PostgrestError } from '@supabase/supabase-js';

function toPostgrestError(error: unknown): PostgrestError {
    return {
        name: 'PostgrestError',
        message: error instanceof Error ? error.message : 'Unknown error',
        details: '',
        hint: '',
        code: 'UNKNOWN',
        toJSON() { return { ...this }; }
    };
}

export const providerMutations = {
    createProvider: async (input: CreateProviderInput) => {
        try {
            if (input.has_sequence_numbers) {
                // Log the attempt
                console.log('Creating sequential provider with input:', {
                    name: input.name,
                    type_id: input.type_id,
                    has_sequence_numbers: input.has_sequence_numbers
                });

                const { data, error } = await supabase.rpc('create_sequential_provider', {
                    p_name: input.name,
                    p_type_id: input.type_id || 0
                });

                if (error) {
                    console.error('Sequential provider creation error details:', {
                        code: error.code,
                        message: error.message,
                        details: error.details,
                        hint: error.hint
                    });

                    if (error.message.includes('already exists')) {
                        throw new ProviderServiceError(
                            'A provider with this name already exists',
                            'DUPLICATE_NAME',
                            error
                        );
                    }

                    if (error.message.includes('Invalid provider type')) {
                        throw new ProviderServiceError(
                            'Invalid provider type selected',
                            'INVALID_TYPE',
                            error
                        );
                    }

                    throw new ProviderServiceError(
                        'Failed to create sequential provider: ' + error.message,
                        'CREATE_ERROR',
                        error
                    );
                }

                console.log('Sequential provider created successfully:', data);
                
                // Update additional fields not handled by the function
                if (data && data.id && (input.currency || input.website_url || input.support_email || input.support_phone || input.free_trial_days)) {
                    const { data: updatedData, error: updateError } = await supabase
                        .from('providers')
                        .update({
                            currency: input.currency,
                            website_url: input.website_url,
                            support_email: input.support_email,
                            support_phone: input.support_phone,
                            free_trial_days: input.free_trial_days
                        })
                        .eq('id', data.id)
                        .select()
                        .single();
                    
                    if (updateError) {
                        console.error('Failed to update additional provider fields:', updateError);
                        // Don't fail the whole operation, just log the error
                    } else {
                        return updatedData;
                    }
                }
                
                return data;
            }

            // Non-sequential provider creation
            console.log('Creating non-sequential provider:', input);
            
            const { data, error } = await supabase
                .from('providers')
                .insert({
                    name: input.name,
                    has_sequence_numbers: false,
                    type_id: input.type_id || null,
                    logo_url: input.logo_url,
                    logo_filename: input.logo_filename,
                    website_url: input.website_url,
                    support_email: input.support_email,
                    support_phone: input.support_phone,
                    free_trial_days: input.free_trial_days,
                    currency: input.currency
                })
                .select('*')
                .single();

            if (error) {
                console.error('Non-sequential provider creation error:', error);
                throw new ProviderServiceError(
                    'Failed to create provider',
                    'CREATE_ERROR',
                    error
                );
            }

            console.log('Non-sequential provider created successfully:', data);
            return data;

        } catch (error) {
            console.error('Provider creation failed:', error);
            
            if (error instanceof ProviderServiceError) {
                throw error;
            }

            // Create a proper error object for unknown errors
            const pgError: PostgrestError = {
                name: 'PostgrestError',
                message: error instanceof Error ? error.message : 'Unknown error',
                details: '',
                hint: '',
                code: 'UNKNOWN',
                toJSON() { return { ...this }; }
            };

            throw new ProviderServiceError(
                'Failed to create provider',
                'CREATE_ERROR',
                pgError
            );
        }
    },

    updateProvider: async (id: number, input: UpdateProviderInput) => {
        try {
            // Check name uniqueness if name is being updated
            if (input.name) {
                const { data: existing } = await supabase
                    .from('providers')
                    .select('id')
                    .eq('name', input.name)
                    .neq('id', id)
                    .single();

                if (existing) {
                    throw new ProviderServiceError(
                        'Provider with this name already exists',
                        'DUPLICATE_NAME'
                    );
                }
            }

            // If updating sequence numbers setting, check existing channels
            if (typeof input.has_sequence_numbers !== 'undefined') {
                const { data: existingChannels, error: checkError } = await supabase
                    .from('channels')
                    .select('id')
                    .eq('provider_id', id)
                    .limit(1);

                if (checkError) {
                    throw new ProviderServiceError(
                        'Failed to check existing channels',
                        'UPDATE_ERROR',
                        checkError
                    );
                }

                if (existingChannels?.length > 0) {
                    throw new ProviderServiceError(
                        'Cannot change sequence number setting after channels have been created',
                        'INVALID_OPERATION'
                    );
                }
            }

            // Perform the update
            const { data, error } = await supabase
                .from('providers')
                .update(input)
                .eq('id', id)
                .select()
                .single();

            if (error) {
                console.error('Provider update error:', error);
                throw new ProviderServiceError(
                    'Failed to update provider',
                    'UPDATE_ERROR',
                    error
                );
            }

            return data as Provider;
        } catch (error) {
            if (error instanceof ProviderServiceError) {
                throw error;
            }
            
            console.error('Provider update failed:', error);
            throw new ProviderServiceError(
                'Failed to update provider',
                'UPDATE_ERROR',
                toPostgrestError(error)
            );
        }
    },

    deleteProvider: async (id: number) => {
        try {
            console.log('Attempting to delete provider:', id);

            // First verify the provider exists
            const { data: provider, error: checkError } = await supabase
                .from('providers')
                .select('id, name')
                .eq('id', id)
                .single();

            if (checkError || !provider) {
                throw new ProviderServiceError(
                    'Provider not found',
                    'NOT_FOUND'
                );
            }

            // Delete import sessions first
            const { error: importSessionError } = await supabase
                .from('import_sessions')
                .delete()
                .eq('provider_id', id);

            if (importSessionError) {
                console.error('Failed to delete import sessions:', importSessionError);
                throw new ProviderServiceError(
                    'Failed to delete provider import sessions',
                    'DELETE_ERROR',
                    importSessionError
                );
            }

            // Now proceed with provider deletion
            const { error } = await supabase.rpc('delete_provider_complete', {
                provider_id_param: id
            });

            if (error) {
                console.error('Delete provider error:', error);
                throw new ProviderServiceError(
                    `Failed to delete provider: ${error.message}`,
                    'DELETE_ERROR',
                    error
                );
            }

            console.log('Provider deleted successfully:', id);
        } catch (error) {
            console.error('Provider deletion failed:', error);
            
            if (error instanceof ProviderServiceError) {
                throw error;
            }
            
            throw new ProviderServiceError(
                'Failed to delete provider',
                'DELETE_ERROR',
                error as PostgrestError
            );
        }
    },

    updateProviderLogo: async (id: number, logoUrl: string, logoFilename: string) => {
        try {
            const { data, error } = await supabase
                .from('providers')
                .update({ 
                    logo_url: logoUrl,
                    logo_filename: logoFilename 
                })
                .eq('id', id)
                .select()
                .single();

            if (error) {
                console.error('Logo update error:', error);
                throw new ProviderServiceError(
                    'Failed to update provider logo',
                    'UPDATE_ERROR',
                    error
                );
            }

            return data as Provider;
        } catch (error) {
            console.error('Logo update failed:', error);
            
            if (error instanceof ProviderServiceError) {
                throw error;
            }
            
            throw new ProviderServiceError(
                'Failed to update provider logo',
                'UPDATE_ERROR',
                toPostgrestError(error)
            );
        }
    }
};